import { useCallback } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { pagesService } from '../services/pagesService';
import { contentService } from '../services/contentService';
import { queryKeys } from '../api/queryClient';
import { syncArticleReactionCaches, syncArticleShareCaches } from '../utils/articleReactions';
import { useAuth } from './useAuth';
import { useDismissableError } from './useDismissableError';

export function useInfiniteFeed(limit = 20, filters = {}) {
  const { applyViewer, user, hydrating } = useAuth();
  const queryClient = useQueryClient();
  const { category, q } = filters;
  const queryKey = queryKeys.feed(limit, {
    category,
    q,
    userId: user?.id ?? null,
  });

  const query = useInfiniteQuery({
    queryKey,
    enabled: !hydrating,
    queryFn: async ({ pageParam }) => {
      const data = await pagesService.feed({
        limit,
        cursor: pageParam,
        category,
        q,
      });
      if (data?.viewer) applyViewer(data.viewer);
      return data;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.meta?.nextCursor ?? undefined,
  });

  const items = query.data?.pages?.flatMap((page) => page?.items ?? []) ?? [];
  const { error, setError } = useDismissableError(query.error);

  const react = useCallback(
    async (article, emoji) => {
      const res = await contentService.react(article.id, emoji);
      syncArticleReactionCaches(queryClient, article.id, res);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'analytics'] });
      return res;
    },
    [queryClient],
  );

  const recordShare = useCallback(
    async (article) => {
      const res = await contentService.share(article.id);
      syncArticleShareCaches(queryClient, article.id, res.shareCount);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'analytics'] });
      return res;
    },
    [queryClient],
  );

  return {
    items,
    loading:
      hydrating ||
      query.isLoading ||
      (query.isFetching && items.length === 0),
    availableCategories: query.data?.pages?.[0]?.meta?.availableCategories ?? [],
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    reload: () => query.refetch(),
    error,
    setError,
    react,
    recordShare,
  };
}
